import {
  addYears,
  addMonths,
  addWeeks,
  endOfYear,
  endOfMonth,
  endOfWeek,
  format,
  formatISO,
  getMonth,
  isToday,
  isTomorrow,
  isWithinInterval,
  parseISO,
  startOfYear,
  startOfMonth,
  startOfWeek,
  subYears,
  subMonths,
  subWeeks,
  subDays,
  endOfDay,
  isDate,
  isBefore,
  isAfter,
  isSameDay,
  differenceInMinutes,
  isThisYear,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useReactAppState } from '../shared/containers/utils';
import dayjs from 'dayjs';
import {
  broadcasterTypes,
  filterTypes,
  getCultureLocalizer,
} from '../shared/utils';
import { isTimeAndDateTBD } from '../mls-stats-comparison/utils';

export const MatchContext = createContext();
MatchContext.displayName = 'Match';

export function useHash() {
  const [hash, setHash] = useState(() => window.location.hash);

  const hashChangeHandler = useCallback(() => {
    setHash(window.location.hash);
  }, []);

  useEffect(() => {
    window.addEventListener('hashchange', hashChangeHandler);
    return () => {
      window.removeEventListener('hashchange', hashChangeHandler);
    };
  }, []);

  const updateHash = useCallback(
    (newHash) => {
      if (newHash !== hash) window.location.hash = newHash;
    },
    [hash]
  );

  return [hash, updateHash];
}

export const getHashQueryObject = (query) => {
  const pairs = query.slice(1).split('&');
  const filters = {};
  pairs.forEach(function (pair) {
    pair = pair.split('=');
    filters[pair[0]] = decodeURIComponent(pair[1] || '');
  });

  return filters;
};

export function useMatchData() {
  const context = useContext(MatchContext);
  if (context === undefined) {
    throw new Error('useMatchData must be used within a <MatchStrip />');
  }
  return context;
}

export const composeAggregate = (aggregate, date) => {
  if (!aggregate) {
    return null;
  }

  if (!date || !aggregate.includes('##date##')) {
    return aggregate;
  }

  return aggregate.replace(/##date##/g, dayjs(date).format('MM/DD/YYYY'));
};

export function getTimeAdjustedByTimeZone(
  dateString,
  timeZoneString = 'America/New_York'
) {
  return utcToZonedTime(`${dateString}`, timeZoneString);
}

function takeCrossSectionOfMonthMatchList({ matchList, weekRange }) {
  const { dateRangeStart, dateRangeEnd } = weekRange || {};

  if (!matchList) {
    return null;
  }

  return matchList.filter((match) => {
    const date = parseISO(match.date);

    return isWithinInterval(date, { start: dateRangeStart, end: dateRangeEnd }) || match.date === 'TBD';
  });
}

export const useMatchList = ({ matchList, dateRangeType, weekRange }) => {
  if (!matchList) {
    return null;
  }

  let month;

  return dateRangeType === dateRangeTypes.week
    ? takeCrossSectionOfMonthMatchList({ matchList, weekRange })
    : dateRangeType === dateRangeTypes.year
    ? matchList
    : matchList.reduce((accum, current) => {
        const parsedDate = parseISO(current.date);
        if (month == null) {
          month = getMonth(parsedDate);
          accum.push({
            date: current.date,
            matches: [...current.matches],
          });

          return accum;
        } else {
          if (month === getMonth(parsedDate)) {
            accum[accum.length - 1].matches.push(...current.matches);
          } else {
            accum.push({
              date: current.date,
              matches: [current.matches],
            });
          }

          return accum;
        }
      }, []);
};

export const useDateDisplay = (date, dateRangeType = dateRangeTypes.week) => {
  const { t } = useReactAppState();
  const parsedDate = parseISO(date);
  return useMemo(() => {
    return dateRangeType === dateRangeTypes.week
      ? isToday(parsedDate)
        ? `${t('today')}, ${format(
            parsedDate,
            isThisYear(parsedDate) ? 'MMM d' : 'MMM d, yyy',
            { locale: getCultureLocalizer() }
          )}`
        : isTomorrow(parsedDate)
        ? `${t('tomorrow')}, ${format(
            parsedDate,
            isThisYear(parsedDate) ? 'MMM d' : 'MMM d, yyy',
            { locale: getCultureLocalizer() }
          )}`
        : format(
            parsedDate,
            isThisYear(parsedDate) ? 'EEEE MMM d' : 'EEEE MMM d, yyy',
            { locale: getCultureLocalizer() }
          )
      : format(parsedDate, 'MMMM yyy', { locale: getCultureLocalizer() });
  }, [date, dateRangeType]);
};

function getTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export const composeDateRangeStartAndEnd = (
  now,
  dateRangeType = dateRangeTypes.week
) => {
  if (!now) {
    return {
      currentRangeYear: null,
      currentRangeMonth: null,
      currentRangeWeek: null,
      nextRangeYear: null,
      nextRangeMonth: null,
      nextRangeWeek: null,
      prevRangeYear: null,
      prevRangeMonth: null,
      prevRangeWeek: null,
      isoStringRS: null,
      isoStringRE: null,
    };
  }

  const timezone = getTimezone();
  const composedNow = utcToZonedTime(now, timezone);

  const { yearRange, monthRange, weekRange } = getDateRange(composedNow);

  const {
    currentRange: currentRangeYear,
    nextRange: nextRangeYear,
    prevRange: prevRangeYear,
  } = yearRange || {};
  const {
    currentRange: currentRangeWeek,
    nextRange: nextRangeWeek,
    prevRange: prevRangeWeek,
  } = weekRange || {};
  const {
    currentRange: currentRangeMonth,
    nextRange: nextRangeMonth,
    prevRange: prevRangeMonth,
  } = monthRange || {};
  const { isoStringRS, isoStringRE } = convertToIsoString(
    dateRangeType === dateRangeTypes.week
      ? currentRangeWeek
      : dateRangeType === dateRangeTypes.year
      ? currentRangeYear
      : currentRangeMonth
  );

  return {
    currentRangeYear,
    currentRangeMonth,
    currentRangeWeek,
    nextRangeYear,
    nextRangeMonth,
    nextRangeWeek,
    prevRangeYear,
    prevRangeMonth,
    prevRangeWeek,
    isoStringRS,
    isoStringRE,
  };
};

export function orderStatsApiMatchesByDate(matchesData) {
  if (!matchesData) {
    return null;
  }

  return matchesData.map((match) => {
    const { date } = match;
    return parseISO(date);
  });
}

export function orderMatchesLiteByDate({ matchesData }) {
  if (!matchesData) {
    return null;
  }

  const orderedMatches = [];

  for (let i = 0; i < matchesData.length; i++) {
    const currentMatch = matchesData[i];

    const { optaId: matchOptaId, matchDate } = currentMatch || {};

    if (!matchOptaId) {
      continue;
    }

    if (!matchDate) {
      continue;
    }

    const toTimeZone = getTimeAdjustedByTimeZone(matchDate);
    const currentDate = format(toTimeZone, 'yyyy-LL-dd');

    const latestOrderedMatchDate = orderedMatches[orderedMatches.length - 1];

    if (
      orderedMatches.length === 0 ||
      latestOrderedMatchDate.date !== currentDate
    ) {
      const matchDay = {
        date: currentDate,
        matches: [currentMatch],
      };

      orderedMatches.push(matchDay);
    } else {
      latestOrderedMatchDate.matches.push(currentMatch);
    }
  }

  return orderedMatches;
}

export function orderEnrichedMatchesByDate({
  dateRangeType = dateRangeTypes.week,
  mlsMatches,
  competitionSportecId,
  clubSportecId,
  isoStringRS,
  isoStringRE,
}) {
  const enrichedMatches = matchesDataLocalTimes(
    mlsMatches,
    isoStringRS,
    isoStringRE
  );
  return orderMatchesByDate({
    dateRangeType,
    mlsMatches: enrichedMatches,
    clubSportecId,
    competitionSportecId,
  });
}

export function matchesDataLocalTimes(mlsMatches, isoStringRS, isoStringRE) {
  if (!mlsMatches) {
    return;
  }

  return mlsMatches
    .map((mlsMatch) => ({
      ...mlsMatch,
      localMatchDate:
        mlsMatch.planned_kickoff_time &&
        parseISO(mlsMatch.planned_kickoff_time),
      localMatchKickOffTime:
        mlsMatch.planned_kickoff_time &&
        parseISO(mlsMatch.planned_kickoff_time),
    }))
    .filter((mlsMatch) => {
      if (!isoStringRS || !isoStringRE) {
        return true;
      } else {
        const day = parseISO(mlsMatch?.planned_kickoff_time);
        return (
          isSameDay(day, parseISO(isoStringRS)) ||
          isSameDay(day, parseISO(isoStringRE)) ||
          (!isBefore(day, parseISO(isoStringRS)) &&
            !isAfter(day, parseISO(isoStringRE)))
        );
      }
    });
}

export function orderMatchesByDate({
  dateRangeType = dateRangeTypes.week,
  mlsMatches,
  competitionSportecId,
  clubSportecId,
  isAllMatches = false,
}) {
  if (!mlsMatches) {
    return null;
  }

  const orderedMatches = [];

  let tbd = {
    date: 'TBD',
    matches: [],
  };

  for (let i = 0; i < mlsMatches.length; i++) {
    const currentMatch = mlsMatches[i];

    const {
      localMatchDate,
      match_id,
      competition_id,
      away_team_id,
      home_team_id,
      match_date_time_status,
      match_status,
    } = currentMatch || {};

    if (!localMatchDate || !match_id) {
      continue;
    }

    if (
      clubSportecId !== filterTypes.all.toLowerCase() &&
      away_team_id !== clubSportecId &&
      home_team_id !== clubSportecId
    ) {
      continue;
    }

    if (
      competitionSportecId !== filterTypes.all.toLowerCase() &&
      competition_id !== competitionSportecId
    ) {
      continue;
    }

    const currentDate = format(currentMatch.localMatchDate, 'yyyy-LL-dd');
    const latestOrderedMatchDate = orderedMatches[orderedMatches.length - 1];
    const currentMonth = format(currentMatch.localMatchDate, 'LL');

    if(match_status === 'scheduled' && match_date_time_status === 'Day and time tbd'){
      if( isTimeAndDateTBD())
      tbd.matches.push(currentMatch);
      continue;
    }
    if (dateRangeType === dateRangeTypes.year) {
      if (
        orderedMatches.length === 0 ||
        latestOrderedMatchDate.month !== currentMonth
      ) {
        const matchDay = {
          date: currentDate,
          month: currentMonth,
          matches: [currentMatch],
        };

        orderedMatches.push(matchDay);
      } else {
        latestOrderedMatchDate.matches.push(currentMatch);
      }
    } else {
      if (
        orderedMatches.length === 0 ||
        latestOrderedMatchDate.date !== currentDate
      ) {
        const matchDay = {
          date: currentDate,
          matches: [currentMatch],
        };

        orderedMatches.push(matchDay);
      } else {
        latestOrderedMatchDate.matches.push(currentMatch);
      }
    }
  }
if(tbd.matches.length>0)
  return [...orderedMatches, tbd];
return orderedMatches;
}

function getDateRange(now) {
  const yearRange = getYearRange(now);
  const weekRange = getWeekRange(now);
  const monthRange = getMonthRange(now);

  return { weekRange, monthRange, yearRange };
}

export function getYear(date) {
  return format(date, 'y');
}

function getWeekRange(now) {
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  const prevWeekStart = subWeeks(weekStart, 1);
  const prevWeekEnd = endOfWeek(prevWeekStart, { weekStartsOn: 1 });

  const nextWeekEnd = addWeeks(weekEnd, 1);
  const nextWeekStart = startOfWeek(nextWeekEnd, { weekStartsOn: 1 });

  const prevRange = getIsoStringWeekEndWeekStart(prevWeekStart, prevWeekEnd);
  const currentRange = getIsoStringWeekEndWeekStart(weekStart, weekEnd);
  const nextRange = getIsoStringWeekEndWeekStart(nextWeekStart, nextWeekEnd);

  return { prevRange, currentRange, nextRange };
}

function getMonthRange(now) {
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);

  const prevMonthStart = subMonths(monthStart, 1);
  const prevMonthEnd = endOfMonth(prevMonthStart);

  const nextMonthEnd = endOfMonth(addMonths(monthEnd, 1));
  const nextMonthStart = startOfMonth(nextMonthEnd);

  const prevRange = getIsoStringMonthEndMonthStart(
    prevMonthStart,
    prevMonthEnd
  );
  const currentRange = getIsoStringMonthEndMonthStart(monthStart, monthEnd);
  const nextRange = getIsoStringMonthEndMonthStart(
    nextMonthStart,
    nextMonthEnd
  );

  return { prevRange, currentRange, nextRange };
}

function getYearRange(now) {
  const yearStart = startOfYear(now);
  const yearEnd = endOfYear(now);

  const prevYearStart = subYears(yearStart, 1);
  const prevYearEnd = endOfYear(prevYearStart);

  const nextYearEnd = addYears(yearEnd, 1);
  const nextYearStart = startOfYear(nextYearEnd);

  const prevRange = getIsoStringWeekEndWeekStart(
    prevYearStart,
    endOfDay(subDays(prevYearEnd, 1))
  );
  const currentRange = getIsoStringWeekEndWeekStart(
    yearStart,
    endOfDay(subDays(yearEnd, 1))
  );
  const nextRange = getIsoStringWeekEndWeekStart(
    nextYearStart,
    endOfDay(subDays(nextYearEnd, 1))
  );

  return { prevRange, currentRange, nextRange };
}

function getIsoStringWeekEndWeekStart(
  weekStart,
  weekEnd,
  timeZone = 'America/Los_Angeles'
) {
  const dateRangeStart = dayjs(weekStart).toDate();
  const dateRangeEnd = dayjs(weekEnd).toDate();

  const monthWeekStart = format(dateRangeStart, 'MMM', {
    locale: getCultureLocalizer(),
  });
  const monthWeekEnd = format(dateRangeEnd, 'MMM', {
    locale: getCultureLocalizer(),
  });
  const dayWeekStart = format(dateRangeStart, 'd');
  const dayWeekEnd = format(dateRangeEnd, 'd');

  const monthWeekStartIntl = format(dateRangeStart, 'MMM', {
    locale: getCultureLocalizer(),
  });
  const monthWeekEndIntl = format(dateRangeEnd, 'MMM', {
    locale: getCultureLocalizer(),
  });

  const formattedDateRangeString =
    monthWeekStart === monthWeekEnd
      ? `${monthWeekStart} ${dayWeekStart} - ${dayWeekEnd}`
      : `${monthWeekStart} ${dayWeekStart} - ${monthWeekEnd} ${dayWeekEnd}`;

  const formattedDateRangeStringIntl =
    monthWeekStartIntl === monthWeekEndIntl
      ? `${monthWeekStartIntl} ${dayWeekStart} - ${dayWeekEnd}`
      : `${monthWeekStartIntl} ${dayWeekStart} - ${monthWeekEndIntl} ${dayWeekEnd}`;

  return {
    dateRangeStart,
    dateRangeEnd,
    formattedDateRangeString,
    formattedDateRangeStringIntl,
  };
}

function getIsoStringMonthEndMonthStart(
  monthStart,
  monthEnd,
  timeZone = 'America/Los_Angeles'
) {
  const dateRangeStart = monthStart;
  const dateRangeEnd = monthEnd;

  const monthWeekStart = format(dateRangeStart, 'MMM', {
    locale: getCultureLocalizer(),
  });
  const monthWeekEnd = format(dateRangeEnd, 'MMM', {
    locale: getCultureLocalizer(),
  });
  const dayWeekStart = format(dateRangeStart, 'd');
  const dayWeekEnd = format(dateRangeEnd, 'd');

  const monthWeekStartIntl = format(dateRangeStart, 'MMM', {
    locale: getCultureLocalizer(),
  });
  const monthWeekEndIntl = format(dateRangeEnd, 'MMM', {
    locale: getCultureLocalizer(),
  });

  const formattedDateRangeString =
    monthWeekStart === monthWeekEnd
      ? `${monthWeekStart} ${dayWeekStart} - ${dayWeekEnd}`
      : `${monthWeekStart} ${dayWeekStart} - ${monthWeekEnd} ${dayWeekEnd}`;

  const formattedDateRangeStringIntl =
    monthWeekStartIntl === monthWeekEndIntl
      ? `${monthWeekStartIntl} ${dayWeekStart} - ${dayWeekEnd}`
      : `${monthWeekStartIntl} ${dayWeekStart} - ${monthWeekEndIntl} ${dayWeekEnd}`;

  return {
    dateRangeStart,
    dateRangeEnd,
    formattedDateRangeString,
    formattedDateRangeStringIntl,
  };
}

export function convertToIsoString({ dateRangeStart, dateRangeEnd }) {
  dateRangeEnd.setDate(dateRangeEnd.getDate() + 1);
  const isoStringRS = formatISO(dateRangeStart, { representation: 'date' });
  const isoStringRE = formatISO(dateRangeEnd, { representation: 'date' });

  return {
    isoStringRS,
    isoStringRE,
  };
}

export const getNearestDate = ({ date, previousDate, nextDate }) => {
  if (!date || (!previousDate && !nextDate)) {
    return null;
  }

  if (!previousDate) {
    return nextDate;
  }

  if (!nextDate) {
    return previousDate;
  }

  const previousDiff = Math.abs(date - previousDate);
  const nextDiff = Math.abs(date - nextDate);

  if (previousDiff < nextDiff) {
    return previousDate;
  } else {
    return nextDate;
  }
};

export const useMediaProviders = ({
  broadcasters,
  homeClubBroadcasters,
  awayClubBroadcasters,
  clubBroadcasters,
}) => {
  const { t } = useReactAppState();
  if (
    broadcasters == null &&
    homeClubBroadcasters == null &&
    awayClubBroadcasters == null &&
    clubBroadcasters == null
  ) {
    return null;
  }

  const broadcastersArray =
    broadcasters && broadcasters.length > 0
      ? broadcasters.map((broadcaster) => {
          if (broadcaster?.broadcasterType) {
            broadcaster.broadcasterTypeLabel = t(
              'brd_type_' +
                broadcaster?.broadcasterType.toLowerCase().replace(/ /g, '_')
            );
          }

          return broadcaster;
        })
      : [];

  const clubBroadcastersArray =
    clubBroadcasters && clubBroadcasters.length > 0
      ? clubBroadcasters.map((broadcaster) => {
          const { broadcasterName, broadcasterType } = broadcaster || {};

          const broadcasterTypeLabel = broadcaster?.broadcasterTypeLabel
            ? broadcaster?.broadcasterTypeLabel
            : broadcasterType === broadcasterTypes.linearTv
            ? t('brd_type_linear_tv')
            : broadcasterType === broadcasterTypes.streaming
            ? t('brd_type_streaming')
            : broadcasterType === broadcasterTypes.radio
            ? t('brd_type_radio')
            : null;

          return {
            ...broadcaster,
            broadcasterTypeLabel,
            broadcasterName:
              broadcasterName != null ? `${broadcasterName}` : null,
          };
        })
      : [];

  const homeClubBroadcastersArray =
    homeClubBroadcasters && homeClubBroadcasters.length > 0
      ? homeClubBroadcasters.map((broadcaster) => {
          const { broadcasterName, broadcasterType } = broadcaster || {};

          const broadcasterTypeLabel =
            broadcasterType === broadcasterTypes.linearTv
              ? t('brd_type_home_linear_tv')
              : null;

          return {
            ...broadcaster,
            broadcasterTypeLabel,
            broadcasterName:
              broadcasterName != null ? `${broadcasterName}` : null,
          };
        })
      : [];

  const awayClubBroadcastersArray =
    awayClubBroadcasters && awayClubBroadcasters.length > 0
      ? awayClubBroadcasters.map((broadcaster) => {
          const { broadcasterName, broadcasterType } = broadcaster || {};

          const broadcasterTypeLabel =
            broadcasterType === broadcasterTypes.linearTv
              ? t('brd_type_away_linear_tv')
              : null;

          return {
            ...broadcaster,
            broadcasterTypeLabel,
            broadcasterName:
              broadcasterName != null ? `${broadcasterName}` : null,
          };
        })
      : [];

  const mergedProviders = [
    ...broadcastersArray,
    ...homeClubBroadcastersArray,
    ...awayClubBroadcastersArray,
    ...clubBroadcastersArray,
  ];

  const providerObj = mergedProviders.reduce((accum, current) => {
    if (current?.broadcasterStreamingURL) {
      if (accum.streamingList && accum.streamingList.length > 0) {
        accum.streamingList.push(current);
      } else {
        accum.streamingList = [current];
      }
    }

    if (current?.broadcasterType === broadcasterTypes.usStreaming) {
      if (accum.usStreaming && accum.usStreaming.length > 0) {
        accum.usStreaming.push(current);
      } else {
        accum.usStreaming = [current];
      }
    }

    if (current?.broadcasterType === broadcasterTypes.canadianStreaming) {
      if (accum.canadianStreaming && accum.canadianStreaming.length > 0) {
        accum.canadianStreaming.push(current);
      } else {
        accum.canadianStreaming = [current];
      }
    }

    if (current?.broadcasterType === broadcasterTypes.usNational) {
      if (accum.usNational && accum.usNational.length > 0) {
        accum.usNational.push(current);
      } else {
        accum.usNational = [current];
      }
    }

    if (current?.broadcasterType === broadcasterTypes.canadianNational) {
      if (accum.canadianNational && accum.canadianNational.length > 0) {
        accum.canadianNational.push(current);
      } else {
        accum.canadianNational = [current];
      }
    }

    if (
      current?.broadcasterType === broadcasterTypes.canadaRadio ||
      current?.broadcasterType === broadcasterTypes.usRadio
    ) {
      if (accum.radio && accum.radio.length > 0) {
        accum.radio.push(current);
      } else {
        accum.radio = [current];
      }
    }

    if (accum.allProvidersList) {
      if (current?.broadcasterTypeLabel) {
        accum.allProvidersList.push(current);
      }
    } else {
      if (current?.broadcasterTypeLabel) {
        accum.allProvidersList = [current];
      }
    }
    return accum;
  }, {});

  const homeProviderObj = homeClubBroadcastersArray.reduce((accum, current) => {
    if (current?.broadcasterType === broadcasterTypes.linearTv) {
      if (accum.homeLinear && accum.homeLinear.length > 0) {
        accum.homeLinear.push(current);
      } else {
        accum.homeLinear = [current];
      }
    }

    return accum;
  }, {});

  const awayProviderObj = awayClubBroadcastersArray.reduce((accum, current) => {
    if (current?.broadcasterType === broadcasterTypes.linearTv) {
      if (accum.awayLinear && accum.awayLinear.length > 0) {
        accum.awayLinear.push(current);
      } else {
        accum.awayLinear = [current];
      }
    }

    return accum;
  }, {});

  const clubProviderObj = clubBroadcastersArray.reduce((accum, current) => {
    if (current?.broadcasterType === broadcasterTypes.linearTv) {
      if (accum.clubLinear && accum.clubLinear.length > 0) {
        accum.clubLinear.push(current);
      } else {
        accum.clubLinear = [current];
      }
    }

    if (current?.broadcasterType === broadcasterTypes.streaming) {
      if (accum.clubStreaming && accum.clubStreaming.length > 0) {
        accum.clubStreaming.push(current);
      } else {
        accum.clubStreaming = [current];
      }
    }

    if (current?.broadcasterType === broadcasterTypes.radio) {
      if (accum.clubRadio && accum.clubRadio.length > 0) {
        accum.clubRadio.push(current);
      } else {
        accum.clubRadio = [current];
      }
    }

    return accum;
  }, {});

  const mergedObj = {
    ...providerObj,
    ...homeProviderObj,
    ...awayProviderObj,
    ...clubProviderObj,
  };

  return isEmpty(mergedObj) ? null : mergedObj;
};

function isEmpty(obj) {
  return Object.keys(obj).length === 0;
}

export function composeMatchStatus({
  period,
  isFinal = false,
  delayedMatch = false,
}) {
  let status = {
    value: period,
  };

  if (delayedMatch) {
    return {
      value: periodTypes.Delayed,
      abbreviation: statusTypes.delayed,
    };
  }

  if (isFinal && period !== periodTypes.Abandoned) {
    return {
      value: 'FullTime',
      abbreviation: statusTypes.post,
    };
  }

  switch (period) {
    case periodTypes.firstHalf:
    case periodTypes.half:
    case periodTypes.secondHalf:
    case periodTypes.preExtra:
    case periodTypes.firstHalfExtra:
    case periodTypes.halfExtra:
    case periodTypes.secondHalfExtra:
    case periodTypes.prePenalty:
    case periodTypes.penalty:
      status = {
        ...status,
        period,
        abbreviation: statusTypes.live,
      };
      break;
    case periodTypes.finalWhistle:
      status = {
        ...status,
        period,
        abbreviation: statusTypes.post,
      };
      break;
    case periodTypes.Abandoned:
      status = {
        ...status,
        abbreviation: statusTypes.abandoned,
      };
      break;
    case periodTypes.Postponed:
      status = {
        ...status,
        abbreviation: statusTypes.postponed,
      };
      break;
    case periodTypes.Cancelled:
      status = {
        ...status,
        abbreviation: statusTypes.cancelled,
      };
      break;
    case periodTypes.preMatch:
    default:
      status = {
        ...status,
        abbreviation: statusTypes.pre,
      };
  }

  return status;
}

export function minutesBeforeMatchStart(date) {
  if (!date || !isDate(date) || date?.toString() === 'Invalid Date') {
    return 0;
  }

  return differenceInMinutes(date, Date.now());
}

export function useMatchDateTime(utcString) {
  if (utcString == null) {
    return null;
  }
  const easternStandardTime = getTimeAdjustedByTimeZone(utcString);
  const time = format(easternStandardTime, 'p');
  const date = format(easternStandardTime, 'M/d');
  const fullDate = format(easternStandardTime, 'M/d/y');
  const matchFullDateTime = format(easternStandardTime, 'PP, p');

  return { time, date, matchFullDateTime, easternStandardTime, fullDate };
}

export function useLocalMatchHour(matchDate) {
  if (matchDate == null) {
    return null;
  }

  const localTime = dayjs(matchDate).toDate();
  const hours = format(localTime, 'h:mm aaa')?.toLowerCase();

  return { hours };
}

export function useLocalMatchDateTime(matchDate) {
  if (matchDate == null) {
    return null;
  }
  const localTime = dayjs(matchDate).toDate();
  const time = format(localTime, 'p');
  const date = format(localTime, 'M/d');
  const fullDate = format(localTime, 'M/d/y');
  const matchFullDateTime = format(localTime, 'PP, p');
  const formattedDateTime = format(localTime, 'MMM d, h:mmaa');
  const formattedDate = format(localTime, 'MMM d');

  return {
    time,
    date,
    matchFullDateTime,
    localTime,
    fullDate,
    formattedDateTime,
    formattedDate
  };
}

export function useVenueData(venueEntity) {
  if (!venueEntity?.[0]?.fields) {
    return null;
  }

  return useMemo(() => {
    const { fields } = venueEntity[0];
    const { name, stateOrProvince, country, city } = fields || {};
    const fullVenueInfo = formatVenue({ name, stateOrProvince, city, country });

    return {
      name,
      stateOrProvince,
      country,
      city,
      fullVenueInfo,
    };
  }, [venueEntity]);
}

export function formatVenue({ name, stateOrProvince, city, country }) {
  const venueArr = [];

  if (name) {
    stateOrProvince || city || country
      ? venueArr.push(`${name},`)
      : venueArr.push(name);
  }

  if (city) {
    stateOrProvince || country
      ? venueArr.push(`${city},`)
      : venueArr.push(city);
  }

  if (stateOrProvince) {
    country
      ? venueArr.push(`${stateOrProvince},`)
      : venueArr.push(stateOrProvince);
  }

  if (country) {
    venueArr.push(country);
  }

  return venueArr ? venueArr.join('') : null;
}

function composeShootout({
  period,
  resultType,
  homeShootoutScore,
  awayShootoutScore,
  homeAbbrev,
  awayAbbrev,
}) {
  if (
    period === periodTypes.ShootOut ||
    period === periodTypes.FullTimePens ||
    resultType === resultTypes.Shootout
  ) {
    if (homeShootoutScore === null && awayShootoutScore === null) {
      return 'Going to PKs';
    }

    const homeFormattedShootoutScore = homeShootoutScore || 0;
    const awayFormattedShootoutScore = awayShootoutScore || 0;

    return homeFormattedShootoutScore === awayFormattedShootoutScore
      ? `${homeAbbrev} ties ${awayAbbrev} ${homeFormattedShootoutScore}:${awayFormattedShootoutScore} on PKs`
      : awayFormattedShootoutScore > homeFormattedShootoutScore
      ? `${awayAbbrev} ${
          period === periodTypes.ShootOut ? 'leads' : 'wins'
        } ${awayFormattedShootoutScore}:${homeFormattedShootoutScore} on PKs`
      : `${homeAbbrev} ${
          period === periodTypes.ShootOut ? 'leads' : 'wins'
        }  ${homeFormattedShootoutScore}:${awayFormattedShootoutScore} on PKs`;
  }

  return null;
}

export function formatScores({
  homeClubData,
  awayClubData,
  period,
  resultType,
}) {
  const { homeScore, homeShootoutScore, abbreviation: homeAbbrev } =
    homeClubData || {};
  const { awayScore, awayShootoutScore, abbreviation: awayAbbrev } =
    awayClubData || {};

  const formattedHomeScore = homeScore === 0 || !homeScore ? 0 : homeScore;
  const formattedAwayScore = awayScore === 0 || !awayScore ? 0 : awayScore;
  const formattedHomeShootoutScore =
    homeShootoutScore === 0 || !homeShootoutScore ? 0 : homeShootoutScore;
  const formattedAwayShootoutScore =
    awayShootoutScore === 0 || !awayShootoutScore ? 0 : awayShootoutScore;

  const shootoutResult = composeShootout({
    period,
    resultType,
    homeShootoutScore,
    awayShootoutScore,
    homeAbbrev,
    awayAbbrev,
  });

  return {
    homeScore: formattedHomeScore,
    awayScore: formattedAwayScore,
    homeShootoutScore: formattedHomeShootoutScore,
    awayShootoutScore: formattedAwayShootoutScore,
    shootoutResult,
  };
}

export function formatMlsMatchScores({ match }) {
  const { match_information } = match || {};
  const { home_team_goals = 0, away_team_goals = 0 } = match_information || {};

  const shootout_result = composeMlsMatchShootout({ match });

  return {
    shootout_result,
    home_score: home_team_goals,
    away_score: away_team_goals,
  };
}

export function composeMlsMatchShootout({ match }) {
  const { match_information, home, away } = match || {};
  const { match_status, home_team_penalty_goals, away_team_penalty_goals } =
    match_information || match || {};

  const homeAbbrev =
    home?.team_three_letter_code ||
    home?.team_short_name ||
    match?.home_team_three_letter_code ||
    '';
  const awayAbbrev =
    away?.team_three_letter_code ||
    away?.team_short_name ||
    match?.away_team_three_letter_code ||
    '';

  if (match_status === periodTypes.prePenalty) {
    return 'Going to PKs';
  }

  if (isNaN(home_team_penalty_goals) || isNaN(away_team_penalty_goals)) {
    return null;
  }

  const isFinalWhistle = match_status === periodTypes.finalWhistle;

  if (home_team_penalty_goals === away_team_penalty_goals) {
    return `${homeAbbrev} ties ${awayAbbrev} ${home_team_penalty_goals}:${away_team_penalty_goals} on PKs`;
  }

  if (home_team_penalty_goals > away_team_penalty_goals) {
    return `${homeAbbrev} ${
      isFinalWhistle ? 'wins' : 'leads'
    } ${home_team_penalty_goals}:${away_team_penalty_goals} on PKs`;
  }

  if (away_team_penalty_goals > home_team_penalty_goals) {
    return `${awayAbbrev} ${
      isFinalWhistle ? 'wins' : 'leads'
    } ${away_team_penalty_goals}:${home_team_penalty_goals} on PKs`;
  }

  return null;
}

export const listTypes = {
  tv: 'tv',
  matches: 'matches',
  odds: 'odds',
};

export const statusTypes = {
  live: 'live',
  post: 'post',
  pre: 'pre',
  abandoned: 'aban',
  postponed: 'ppd',
  cancelled: 'canc',
  delayed: 'dly',
  scheduled: 'scheduled',
  preMatch: 'preMatch',
  firstHalf: 'firstHalf',
  half: 'half',
  secondHalf: 'secondHalf',
  preExtra: 'preExtra',
  firstHalfExtra: 'firstHalfExtra',
  halfExtra: 'halfExtra',
  secondHalfExtra: 'secondHalfExtra',
  prePenalty: 'prePenalty',
  penalty: 'penalty',
  finalWhistle: 'finalWhistle',
};

export const liveStatusTypes = {
  live: 'live',
  firstHalf: 'firstHalf',
  half: 'half',
  secondHalf: 'secondHalf',
  preExtra: 'preExtra',
  firstHalfExtra: 'firstHalfExtra',
  halfExtra: 'halfExtra',
  secondHalfExtra: 'secondHalfExtra',
  prePenalty: 'prePenalty',
  penalty: 'penalty',
};

export const dateRangeTypes = {
  year: 'year',
  month: 'month',
  week: 'week',
};

export const periodTypes = {
  PreMatch: 'PreMatch',
  FirstHalf: 'FirstHalf',
  HalfTime: 'HalfTime',
  SecondHalf: 'SecondHalf',
  FullTime90: 'FullTime90',
  ExtraFirstHalf: 'ExtraFirstHalf',
  ExtraHalfTime: 'ExtraHalfTime',
  ExtraSecondHalf: 'ExtraSecondHalf',
  ShootOut: 'ShootOut',
  FullTimePens: 'FullTimePens',
  FullTime: 'FullTime',
  Abandoned: 'Abandoned',
  Postponed: 'Postponed',
  Cancelled: 'Cancelled',
  Delayed: 'Delayed',
  PenaltyShootout: 'PenaltyShootout',
  scheduled: 'scheduled',
  preMatch: 'preMatch',
  half: 'half',
  penalty: 'penalty',
  prePenalty: 'prePenalty',
  preExtra: 'preExtra',
  halfExtra: 'halfExtra',
  finalWhistle: 'finalWhistle',
  firstHalf: 'firstHalf',
  half: 'half',
  secondHalf: 'secondHalf',
  preExtra: 'preExtra',
  firstHalfExtra: 'firstHalfExtra',
  halfExtra: 'halfExtra',
  secondHalfExtra: 'secondHalfExtra',
  prePenalty: 'prePenalty',
  penalty: 'penalty',
  finalWhistle: 'finalWhistle',
  secondHalf: 'secondHalf',
  secondHalfExtra: 'secondHalfExtra',
};

export const resultTypes = {
  NormalResult: 'NormalResult',
  AfterExtraTime: 'AfterExtraTime',
  Abandoned: 'Abandoned',
  Shootout: 'PenaltyShootout',
  Cancelled: 'Cancelled',
  Aggregate: 'Aggregate',
  AwayGoals: 'AwayGoals',
};

export const matchTypes = {
  FirstLeg: '1st Leg',
  SecondLeg: '2nd Leg',
  SecondLegAway: '2nd Leg Away Goal',
  Cup: 'Cup',
  CupGold: 'Cup Gold',
  CupShort: 'Cup Short',
  Regular: 'Regular',
  SecondLegCupShort: '2nd Leg Cup Short',
};

export function composeLeg(leg, t, isScoreboard = false) {
  if (!leg) {
    return '';
  }

  if (leg === matchTypes.FirstLeg) {
    if (isScoreboard) {
      return t('l1');
    }
    return t('1st_leg');
  } else if (leg === matchTypes.SecondLeg) {
    if (isScoreboard) {
      return t('l2');
    }
    return t('2nd_leg');
  } else if (leg === matchTypes.Cup) {
    if (isScoreboard) {
      return t('c');
    }
    return t('cup');
  } else if (leg === matchTypes.CupGold) {
    if (isScoreboard) {
      return t('cg');
    }
    return t('cup_gold');
  } else if (leg === matchTypes.CupShort) {
    if (isScoreboard) {
      return t('cs');
    }
    return t('cup_short');
  } else if (leg === matchTypes.Regular) {
    if (isScoreboard) {
      return t('r');
    }
    return t('regular');
  } else if (leg === matchTypes.SecondLegCupShort) {
    if (isScoreboard) {
      return t('l2cs');
    }
    return t('2nd_leg_cup_short');
  } else if (leg === matchTypes.SecondLegAway) {
    if (isScoreboard) {
      return t('l2aG');
    }
    return t('2nd_leg_away_goal');
  } else {
    return leg;
  }
}

export const getMatchListOdds = (oddsData) => {
  let data = {};
  if (!oddsData || !oddsData?.length) {
    return null;
  }

  const range = (firstNum, secondNum) =>
    [
      ...Array(
        firstNum >= secondNum ? firstNum - secondNum : secondNum - firstNum
      ).keys(),
    ].map((i) => i + (firstNum <= secondNum ? firstNum : secondNum)).length;

  const market = oddsData[0]?.markets?.find((odd) => {
    return (
      odd?.marketType === '3way' &&
      odd?.name?.text === 'Match Result' &&
      odd?.isDisplayed === true
    );
  });

  const overUnderMarket = oddsData[0].markets
    ?.filter((odd) => {
      return (
        odd?.marketType === 'Over/Under' &&
        odd?.name?.text === 'Total Goals' &&
        odd?.isDisplayed === true
      );
    })
    .reduce(
      (accumulator, market) => {
        const overUnderOptions = market?.options;
        const overOption = overUnderOptions?.find(
          (option) =>
            option?.name?.text?.includes('Over') && option?.isDisplayed === true
        );
        const underOption = overUnderOptions?.find(
          (option) =>
            option?.name?.text?.includes('Under') &&
            option?.isDisplayed === true
        );
        const overPrice = overOption?.price?.usOdds;
        const underPrice = underOption?.price?.usOdds;
        if (typeof overPrice === 'number' && typeof underPrice === 'number') {
          const rangePrice = range(overPrice, underPrice);
          if (accumulator?.range === null || rangePrice <= accumulator?.range) {
            accumulator = { range: rangePrice, market };
            return accumulator;
          } else {
            return accumulator;
          }
        } else {
          return accumulator;
        }
      },
      { range: null }
    )?.market;

  const options = market ? market?.options : [];
  const overUnderOptions = overUnderMarket?.options || [];

  if (market && options.length) {
    const homeOption = options[0] && options[0].isDisplayed ? options[0] : null;
    const tieOption =
      options[1] && options[1].isDisplayed && options[1].name.text === 'X'
        ? options[1]
        : null;
    const awayOption =
      options[2] && options[2].isDisplayed
        ? options[2]
        : options[1] && options[1].name.text !== 'X'
        ? options[1]
        : null;

    if (homeOption && awayOption && tieOption) {
      const homePrice = homeOption?.price?.usOdds;
      const awayPrice = awayOption?.price?.usOdds;
      const tiePrice = tieOption?.price?.usOdds;

      data = {
        homePrice: homePrice
          ? homePrice < 0
            ? `${homePrice}`
            : `+${homePrice}`
          : '0',
        tiePrice: tiePrice
          ? tiePrice < 0
            ? `${tiePrice}`
            : `+${tiePrice}`
          : '0',
        awayPrice: awayPrice
          ? awayPrice < 0
            ? `${awayPrice}`
            : `+${awayPrice}`
          : '0',
        overPrice: '0',
        underPrice: '0',
      };
    }
  }

  if (overUnderMarket && overUnderOptions.length) {
    const properties = ['0,5', '1,5', '2,5', '3,5', '4,5', '5,5'];

    const overOption = overUnderOptions?.find(
      (option) =>
        option?.name?.text?.includes('Over') && option?.isDisplayed === true
    );
    const underOption = overUnderOptions?.find(
      (option) =>
        option?.name?.text?.includes('Under') && option?.isDisplayed === true
    );
    const property = properties?.find(
      (prop) =>
        overOption?.name?.text?.includes(prop) ||
        underOption?.name?.text?.includes(prop)
    );

    if (overOption && underOption) {
      const overPrice = overOption?.price?.usOdds;
      const underPrice = underOption?.price?.usOdds;

      data.overPrice = overPrice
        ? overPrice < 0
          ? `${overPrice}`
          : `+${overPrice}`
        : '0';
      data.underPrice = underPrice
        ? underPrice < 0
          ? `${underPrice}`
          : `+${underPrice}`
        : '0';
      data.totalGoals = property?.replace(',', '.');
    }
  }

  return data;
};

export const getPhaseTypeFromSlug = (competitions, competitionSlug) => {
  return competitions?.find((comp) => comp?.slug === competitionSlug)
    ?.mlsPhaseType;
};

export function isPromoLinkValid(callToAction = {}) {
  if (callToAction?.url && callToAction?.displayText) {
    return true;
  }

  return false;
}

export function isMatchPromoValid(matchPromo = {}) {
  return (
    matchPromo.headline ||
    matchPromo.description ||
    isPromoLinkValid(matchPromo.callToAction1) ||
    isPromoLinkValid(matchPromo.callToAction2)
  );
}

export function slugifyMatch(str) {
  if (!str) {
    return '';
  }

  const slug = str.toLowerCase().split(' ').join('_');

  return 'match_' + slug;
}

// mls
export function mergeMlsAndD3Matches({ mlsMatches, d3Matches }) {
  const d3MatchesList = new Map(
    d3Matches?.map((match) => [match?.sportecId, match])
  );

  return mlsMatches?.map((match) => {
    const d3Match = d3MatchesList.get(match?.match_id);
    return { ...match, ...d3Match };
  });
}

export function composeForgeClubs(clubs = []) {
  const allClubsOption = {
    slug: null,
    name: filterTypes.allClubs,
    shortName: filterTypes.allClubs,
    sportecId: filterTypes.all.toLowerCase(),
    abbreviation: filterTypes.allClubs,
    backgroundColor: null,
    logoBWSlug: null,
    logoColorSlug: null,
    logoColorUrl: null,
    crestColorSlug: null,
  };

  const clubsList = clubs?.map((club) => {
    return {
      slug: club?.slug,
      name: club?.fields?.name,
      shortName: club?.fields?.shortName,
      sportecId: club?.fields?.sportecId,
      abbreviation: club?.fields?.abbreviation,
      backgroundColor: club?.fields?.backgroundColor,
      logoBWSlug: club?.fields?.logoBWSlug,
      logoColorSlug: club?.fields?.logoColorSlug,
      logoColorUrl: club?.fields?.logoColorUrl,
      crestColorSlug: club?.fields?.crestColorSlug,
    };
  });

  return [allClubsOption, ...clubsList];
}

export function hasConfiguredCompetitionSelection(
  competitions,
  competitionsEditorialSelectionSlug
) {
  return Array.isArray(competitions?.items) || !!competitionsEditorialSelectionSlug;
}

export function getConfiguredCompetitionSportecIds(competitions) {
  return (competitions?.items || [])
    .map((competition) => competition?.fields?.sportecId)
    .filter(Boolean);
}

export function composeConfiguredMlsCompetitions(
  competitions,
  allCompetitionsLabel = filterTypes.allCompetitions
) {
  const configuredCompetitions = (competitions?.items || [])
    .filter((competition) => !!competition?.fields?.sportecId)
    .map((competition) => {
      const { fields = {}, title } = competition || {};
      const { sportecId, name } = fields;

      return {
        competition_id: sportecId,
        competition_name: title || name || sportecId,
      };
    });

  return [
    {
      competition_name: allCompetitionsLabel,
      competition_id: filterTypes.all.toLowerCase(),
    },
    ...configuredCompetitions,
  ];
}

export function isAllOptionSelected(value) {
  return value?.toLowerCase()?.includes('all');
}

export function composeAllOption(value) {
  if (isAllOptionSelected(value)) {
    return filterTypes.all?.toLowerCase();
  }

  return value;
}

export function normalizeConfiguredCompetitionSportecId(
  value,
  configuredCompetitionSportecIds
) {
  const competitionSportecId = composeAllOption(value);

  if (
    !Array.isArray(configuredCompetitionSportecIds) ||
    isAllOptionSelected(competitionSportecId)
  ) {
    return competitionSportecId;
  }

  return configuredCompetitionSportecIds.includes(competitionSportecId)
    ? competitionSportecId
    : filterTypes.all.toLowerCase();
}

export function filterMlsMatchesByCompetitionsToSkip(mlsMatches) {
  const competitionsToSkip = getCompetitionsToSkip();

  return mlsMatches?.filter((match) => {
    if (competitionsToSkip?.includes(match?.competition_id)) {
      return false;
    }

    return true;
  });
}

export function filterMlsMatchesByConfiguredCompetitions(
  mlsMatches,
  configuredCompetitionSportecIds
) {
  if (!Array.isArray(configuredCompetitionSportecIds)) {
    return mlsMatches;
  }

  const configuredCompetitionIds = new Set(configuredCompetitionSportecIds);

  return mlsMatches?.filter((match) =>
    configuredCompetitionIds.has(match?.competition_id)
  );
}

export function getCompetitionsToSkip() {
  const { mlsCompetitionsToSkip } = window?.forgeVariables?.match || {};

  if (!mlsCompetitionsToSkip) {
    return [];
  }

  return mlsCompetitionsToSkip?.split(',');
}

export function getWhiteListCompetitions(competitions) {
  const competitionsToSkip = getCompetitionsToSkip();

  return competitions?.filter(
    (competition) => !competitionsToSkip?.includes(competition?.competition_id)
  );
}

export function getDefaultMlsCompetitionId() {
  const { mlsDefaultCompetitionId = '' } = window?.forgeVariables?.match || {};

  return mlsDefaultCompetitionId;
}

export function getCurrentDatePoint(date) {
  const currentDatePoint = isDate(date) ? date : new Date(date);

  return currentDatePoint;
}

export function getFirstDateOfYear(date = new Date()?.getFullYear()) {
  const initialDate = startOfYear(new Date(date, 0));
  return initialDate;
}

export function getLastDateOfYear(date = new Date()?.getFullYear()) {
  const lastDate = endOfYear(new Date(date, 0));
  return lastDate;
}

export function getFirstDateOfMonth(date = new Date()) {
  const initialDate = startOfMonth(date);
  return initialDate;
}

export function getLastDateOfMonth(date = new Date()) {
  const lastDate = endOfMonth(date);
  return lastDate;
}

export function getSeasonLastYear(seasonLastYear) {
  const currentYear = getYear(new Date());
  // HOTFIX
  // If the latest season is the current year (or defaults to it), extend range to next year
  if (!seasonLastYear || String(seasonLastYear) === String(currentYear)) {
    return endOfYear(addYears(new Date(), 1));
  }

  return getLastDateOfYear(seasonLastYear);
}

export function getNewCurrentDatePoint({
  currentDatePoint,
  hashQueryObject,
  seasonFirstYear,
  seasonLastYear,
}) {
  const formattedCurrentDatePoint = format(
    new Date(currentDatePoint),
    'yyyy-MM-dd'
  );
  let newCurrentDatePoint =
    formattedCurrentDatePoint === hashQueryObject?.date
      ? currentDatePoint
      : new Date(hashQueryObject.date || currentDatePoint);

  if (
    isBefore(newCurrentDatePoint, seasonFirstYear) ||
    isAfter(newCurrentDatePoint, seasonLastYear)
  ) {
    newCurrentDatePoint = new Date();
  }

  return newCurrentDatePoint;
}
